import { BarcodeOutlined, CreditCardOutlined, QrcodeOutlined } from "@ant-design/icons";
import { Alert, Button, Form, FormInstance, FormListFieldData } from "antd";
import { useEffect, useState } from "react";
import CreditCard from "../../components/Form/Payment/CreditCard";
import { enPaymentMethod } from "../../enums/enPaymentMethod";
import { ISubscription, ISubscriptionPaymentMethod } from "../../interfaces/ISubscription";

type Props = {
  subscription: ISubscription;
  name: (number | string)[];
  field: FormListFieldData;
  form: FormInstance;
}

const paymentNameByType = {
  [enPaymentMethod.CREDIT_CARD]: 'Cartão de Crédito',
  [enPaymentMethod.PIX]: 'Pix',
  [enPaymentMethod.BOLETO]: 'Boleto',
} as { [key: string]: string };

const paymentIconByType = {
  credit_card: <CreditCardOutlined style={{ fontSize: 16, marginRight: 5 }} />,
  pix: <QrcodeOutlined style={{ fontSize: 16, marginRight: 5 }} />,
  boleto: <BarcodeOutlined style={{ fontSize: 16, marginRight: 5 }} />,
} as { [key: string]: JSX.Element };

const PaymentMethod = ({ form, field, name }: Props) => {
  const { key: fieldKey, ...restField } = field;
  const payment = Form.useWatch(name, form) as ISubscriptionPaymentMethod & { isEditing: boolean; isNew: boolean };
  const [oldPaymentMethod, setOldPaymentMethod] = useState<string>(payment?.paymentMethod);
  const availablePaymentMethods = [enPaymentMethod.CREDIT_CARD, enPaymentMethod.PIX, enPaymentMethod.BOLETO];

  const canShowCreditCard = availablePaymentMethods.includes(enPaymentMethod.CREDIT_CARD);
  const canShowPix = availablePaymentMethods.includes(enPaymentMethod.PIX);
  const canShowBoleto = availablePaymentMethods.includes(enPaymentMethod.BOLETO);
  const hasOnlyOnePaymentMethodShown = [canShowBoleto, canShowPix, canShowCreditCard].filter(Boolean).length === 1;

  useEffect(() => {
    if (oldPaymentMethod) {
      return;
    }

    setOldPaymentMethod(payment?.paymentMethod);
  }, [payment?.paymentMethod]);

  if (!payment) {
    return null;
  }

  const setAsEditing = () => {
    form.setFieldValue([...name, 'isEditing'], !payment.isEditing);
  };

  const changePaymentMethod = (method: string) => {
    form.setFieldValue([...name, 'paymentMethod'], method);
  };

  const hasChangingPaymentMethod = oldPaymentMethod !== payment?.paymentMethod && !payment?.isNew;

  return (
    <div className='border-2 border-gray-200 p-4 rounded-lg'>
      <div>
        <div className="flex items-center gap-2 justify-between">
          <div>
            <p className="font-medium text-gray-500">
              {paymentIconByType[payment.paymentMethod]} {paymentNameByType[payment.paymentMethod]}
            </p>

            {payment.paymentMethod == enPaymentMethod.CREDIT_CARD && !payment.isEditing && (
              <div>
                <p className="text-sm mt-1 text-gray-500">
                  {payment.cardInfo?.brand} {payment.cardInfo?.firstDigits} **** **** {payment.cardInfo?.lastDigits}
                </p>
              </div>
            )}
          </div>

          {!payment.isEditing && (
            <Button size='small' color="default" variant="outlined" onClick={setAsEditing}>
              Alterar
            </Button>
          )}

          {payment.isEditing && (
            <Button size='small' color="default" variant="outlined" onClick={setAsEditing}>
              Cancelar
            </Button>
          )}
        </div>
      </div>

      {payment.isEditing && (
        <div className='mt-4'>
          <div className="w-full">
            <Form.Item
              key={fieldKey}
              {...restField}
              name={[field.name, 'paymentMethod']}
              rules={[{ required: true, message: 'Selecione um tipo de pagamento' }]}
              hidden={hasOnlyOnePaymentMethodShown}
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {canShowCreditCard && (
                  <div className="flex items-center justify-center">
                    <Button
                      color={payment.paymentMethod === 'credit_card' ? 'primary' : 'default'}
                      icon={<CreditCardOutlined />}
                      variant="outlined"
                      block
                      onClick={() => changePaymentMethod('credit_card')}
                    >
                      Cartão
                    </Button>
                  </div>
                )}

                {canShowPix && (
                  <div className="flex items-center justify-center">
                    <Button
                      color={payment.paymentMethod === 'pix' ? 'primary' : 'default'}
                      icon={<QrcodeOutlined />}
                      variant="outlined"
                      block
                      onClick={() => changePaymentMethod('pix')}
                    >
                      PIX
                    </Button>
                  </div>
                )}

                {canShowBoleto && (
                  <div className="flex items-center justify-center">
                    <Button
                      color={payment.paymentMethod === 'boleto' ? 'primary' : 'default'}
                      icon={<BarcodeOutlined />}
                      variant="outlined"
                      block
                      onClick={() => changePaymentMethod('boleto')}
                    >
                      Boleto
                    </Button>
                  </div>
                )}
              </div>
            </Form.Item>
          </div>

          {hasChangingPaymentMethod && (
            <Alert
              message={`Atenção, você está alterando a forma de pagamento de ${paymentNameByType[oldPaymentMethod]} para ${paymentNameByType[payment.paymentMethod]}.`}
              type="warning"
              showIcon
            />
          )}

          <div>
            {payment.paymentMethod == enPaymentMethod.CREDIT_CARD && (
              <CreditCard form={form} name={[field.name, 'creditCard']} disableInstallments basePath="payments" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethod;
